import { GridApi, IRowNode } from 'ag-grid-community';
import { isNil } from 'lodash';

import { IQuotePart } from 'quotes/types';

export type IsLineEditable = (quotePart: IQuotePart) => boolean;

// Display order, so the grid's current sort, filter and expanded groups decide what "next" is.
export const findEditableLineFrom = (
  gridApi: GridApi,
  startIndex: number,
  isLineEditable: IsLineEditable,
  skipLineId: number = null
): IQuotePart | null => {
  if (!gridApi || null == startIndex || startIndex < 0) {
    return null;
  }

  const displayedRowCount = gridApi.getDisplayedRowCount();

  for (let index = startIndex; index < displayedRowCount; index++) {
    const node: IRowNode<IQuotePart> = gridApi.getDisplayedRowAtIndex(index);
    const line = node?.data;

    if (!line || node.group || line.id === skipLineId) {
      continue;
    }

    if (isLineEditable(line)) {
      return line;
    }
  }

  return null;
};

export const findNextEditableLine = (
  gridApi: GridApi,
  currentLineId: number,
  isLineEditable: IsLineEditable
): IQuotePart | null => {
  if (!gridApi || null == currentLineId) {
    return null;
  }

  const currentRowIndex = gridApi.getRowNode(String(currentLineId))?.rowIndex;

  if (null == currentRowIndex) {
    return null;
  }

  return findEditableLineFrom(gridApi, currentRowIndex + 1, isLineEditable, currentLineId);
};

// The save can re-sort or re-filter the grid, so the line picked before it is looked up again.
export const resolveLineAfterSave = (
  gridApi: GridApi,
  plannedLineId: number,
  plannedRowIndex: number,
  savedLineId: number,
  isLineEditable: IsLineEditable
): IQuotePart | null => {
  const plannedNode: IRowNode<IQuotePart> = gridApi?.getRowNode(String(plannedLineId));

  if (plannedNode?.data && null != plannedNode.rowIndex && isLineEditable(plannedNode.data)) {
    return plannedNode.data;
  }

  return findEditableLineFrom(gridApi, plannedRowIndex, isLineEditable, savedLineId);
};

// Centres the line only when it is not already fully in view, so a visible line does not jump.
export const scrollLineIntoView = (gridApi: GridApi, lineId: number) => {
  if (!gridApi || gridApi.isDestroyed() || isNil(lineId)) {
    return;
  }

  const node = gridApi.getRowNode(String(lineId));

  // Filtered out or inside a collapsed group: not displayed, nothing to scroll to.
  if (!node || isNil(node.rowIndex)) {
    return;
  }

  const { top, bottom } = gridApi.getVerticalPixelRange();
  const isFullyVisible =
    !isNil(node.rowTop) && node.rowTop >= top && node.rowTop + (node.rowHeight ?? 0) <= bottom;

  if (!isFullyVisible) {
    gridApi.ensureIndexVisible(node.rowIndex, 'middle');
  }
};
